/**
 * EIP-712 typing of an Aile intent. The domain MUST stay byte-identical to the
 * backend's `src/lib/eip712.ts`, or every signature fails verification there.
 * Chain id and LOP come from the descriptor: while either is unknown the
 * builders return `null` and the caller refuses to sign.
 */

import type { Address, TypedDataDomain } from "viem";
import type { LopOrder } from "./api";
import { getChainId, getLopAddress } from "./runtimeConfig";

export const EIP712_DOMAIN_NAME = "Aile Intent Protocol";
export const EIP712_DOMAIN_VERSION = "1";

/** Field list of the LOP v4 `Order` struct, in signing order. */
export const ORDER_TYPE = [
  { name: "salt", type: "uint256" },
  { name: "maker", type: "address" },
  { name: "receiver", type: "address" },
  { name: "makerAsset", type: "address" },
  { name: "takerAsset", type: "address" },
  { name: "makingAmount", type: "uint256" },
  { name: "takingAmount", type: "uint256" },
  { name: "makerTraits", type: "uint256" },
] as const;

/** EIP-712 domain fields, required by `eth_signTypedData_v4` payloads. */
export const EIP712_DOMAIN_TYPE = [
  { name: "name", type: "string" },
  { name: "version", type: "string" },
  { name: "chainId", type: "uint256" },
  { name: "verifyingContract", type: "address" },
] as const;

/** Domain from runtime config, or null when the chain id or the LOP address is not known. */
export function orderDomain(): TypedDataDomain | null {
  const chainId = getChainId();
  const verifyingContract = getLopAddress();
  if (chainId === null || verifyingContract === null) return null;
  return {
    name: EIP712_DOMAIN_NAME,
    version: EIP712_DOMAIN_VERSION,
    chainId,
    verifyingContract: verifyingContract as Address,
  };
}

/** Payload shape `eth_signTypedData_v4` is JSON-stringified from. */
export interface OrderTypedData {
  domain: TypedDataDomain;
  types: {
    EIP712Domain: typeof EIP712_DOMAIN_TYPE;
    Order: typeof ORDER_TYPE;
  };
  primaryType: "Order";
  message: LopOrder;
}

/**
 * Null while the chain id or LOP is unknown. Amounts stay decimal strings, as
 * the wallet's JSON-RPC and the backend expect for uint256 fields.
 */
export function buildOrderTypedData(order: LopOrder): OrderTypedData | null {
  const domain = orderDomain();
  if (domain === null) return null;
  return {
    domain,
    types: { EIP712Domain: EIP712_DOMAIN_TYPE, Order: ORDER_TYPE },
    primaryType: "Order",
    message: order,
  };
}
